// @ts-check
import {Text, Toggle, Tag} from '@/components-v2/atoms'
import {Dropdown, SectionTitle, TextArea, TextInput} from '@/components-v2/molecules'
import {PromotionAppPreview} from './PromotionAppPreview'
import {PromotionImageUpload} from './PromotionImageUpload'
import {DISPLAY_ORDER_OPTIONS, LOCATION_OPTIONS} from '@/utils/constants/promotionConstants.js'
import {usePromotionBannerUpload} from './usePromotionBannerUpload'
import {usePromotionDisplay} from './usePromotionDisplay'
import {formatDate} from '@/helpers/dateUtils'
import './_PromotionDisplay.scss'

export const PromotionDisplayStep = () => {
  const {handleBannerUpload} = usePromotionBannerUpload()
  const {
    promotion,
    languages,
    displayFirst,
    bannerTypeOptions,
    landingPageOptions,
    showsOnPlaceholder,
    getDisplayOnValue,
    getDisplayOrderValue,
    getBannerTypeValue,
    getLandingPageValue,
    handleTextChange,
    handleDropdownChange,
    handleDisplayFirstChange,
  } = usePromotionDisplay()

  // uncomment if expiration date will need to update on promotion preview with the promotion's active end date
  // const expirationDate = promotion?.activeEndDate ? formatDate(promotion?.activeEndDate) : null

  return (
    <div className="mt-4 rounded-lg p-4">
      {/* Banner Section */}
      <div className="flex flex-col gap-6 rounded-lg bg-[var(--color-background-layer-01)] p-6">
        <div className="flex flex-col gap-6 rounded-lg border border-[var(--color-border-default)] p-6">
          <Text headingLevel={6}>Promotion Image*</Text>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex h-full min-w-0 flex-col [&>div]:w-full [&>div]:flex-1">
              <div className="flex flex-col gap-4">
                <Text size="small">Upload</Text>
                <PromotionImageUpload onImageSelect={handleBannerUpload} value={promotion?.bannerUrl}/>
                {promotion?.bannerUrl ? null : (
                  <Tag
                    text="No promotional text in the image"
                    size="large"
                    isCopyable={false}
                    className="self-start"
                    style={{backgroundColor: '#5E350A'}}
                    iconLeading="warning_amber"
                  />
                )}
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-4 border-t border-[var(--color-border-default)] pt-6 md:border-l md:border-t-0 md:pl-6 md:pt-0">
              <Text size="small">Image Preview in App</Text>
              <PromotionAppPreview
                bannerUrl={promotion?.bannerUrl}
                promotionTitle={promotion?.text?.en?.content}
                promotionDescription={promotion?.text?.en?.bannerText}
                promotionExcerpt={promotion?.text?.en?.excerpt}
                // expiryDate={expirationDate}
              />
            </div>
          </div>

          <div className="flex flex-row items-end gap-6">
            <Dropdown
              childList={LOCATION_OPTIONS}
              label="Promotion Shows On"
              placeholder={showsOnPlaceholder || 'Select Option'}
              selection="multiple"
              size="medium"
              selectedValue={getDisplayOnValue()}
              setSelectedValue={value => handleDropdownChange('displayOn', value)}
            />

            <Dropdown
              childList={bannerTypeOptions}
              isClearable={false}
              label="Banner Type"
              placeholder="Select Banner Type"
              selection="single"
              size="medium"
              selectedValue={getBannerTypeValue()}
              setSelectedValue={value => handleDropdownChange('bannerType', value)}
            />
            {promotion.bannerType === 'clickable' && (
              <Dropdown
                childList={landingPageOptions}
                isClearable={false}
                label="Landing Page *"
                placeholder="Select Landing Page"
                selection="single"
                size="medium"
                selectedValue={getLandingPageValue()}
                setSelectedValue={value => handleDropdownChange('landingPage', value)}
              />
            )}
          </div>

          <div className="flex flex-col gap-4">
            <Toggle
              label="Display First"
              value="Yes"
              checked={displayFirst}
              size="medium"
              variant="horizontal"
              onUpdate={handleDisplayFirstChange}
            />
            {!displayFirst && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:w-2/3">
                <Dropdown
                  childList={DISPLAY_ORDER_OPTIONS}
                  label="Display Order"
                  placeholder="Display Order"
                  selection="single"
                  size="medium"
                  selectedValue={getDisplayOrderValue()}
                  setSelectedValue={value => handleDropdownChange('displayOrder', value)}
                />
              </div>
            )}
          </div>
        </div>

        {/* <div className="flex flex-row gap-6">
          <Dropdown
            childList={routeTypeIdOptions}
            isClearable={false}
            label="Route Type ID"
            placeholder="Select Route Type ID"
            selection="single"
            size="medium"
            selectedValue={routeTypeIdSelected}
            setSelectedValue={value => handleDropdownChange('routeTypeId', value)}
          />
        </div> */}

        <div className="border-t border-t-[var(--color-border-default)]"></div>

        {/* Text Section */}
        <SectionTitle label="Promotion Text" />

        <div className="grid grid-cols-3 gap-4">
          {languages.map(lang => {
            const language = lang.code

            return (
              <div key={language} className="space-y-5">
                <h3 className="font-semibold">{lang.language}</h3>
                <TextInput
                  label="Product Tag*"
                  onUpdate={value => handleTextChange(value, language, 'productTag')}
                  placeholderText="10% Discount"
                  value={promotion?.text?.[language]?.productTag || ''}
                />
                <TextInput
                  label={'Banner Tag*'}
                  onUpdate={value => handleTextChange(value, language, 'bannerTag')}
                  placeholderText="10% Discount"
                  value={promotion?.text?.[language]?.bannerTag || ''}
                />
                <TextInput
                  label="Excerpt"
                  onUpdate={value => handleTextChange(value, language, 'excerpt')}
                  placeholderText="Excerpt"
                  value={promotion?.text?.[language]?.excerpt || ''}
                />
                <TextInput
                  label="Title*"
                  onUpdate={value => handleTextChange(value, language, 'title')}
                  placeholderText="Get 10% off when buying any 5 Lays or Doritos"
                  value={promotion?.text?.[language]?.title || ''}
                />
                <TextInput
                  label="Content"
                  onUpdate={value => handleTextChange(value, language, 'content')}
                  placeholderText="Content"
                  value={promotion?.text?.[language]?.content || ''}
                />
                <TextInput
                  label="Image Text"
                  onUpdate={value => handleTextChange(value, language, 'bannerText')}
                  placeholderText="Image Text"
                  value={promotion?.text?.[language]?.bannerText || ''}
                />
                <TextArea
                  label="Description"
                  maxLength={500}
                  onUpdate={value => handleTextChange(value, language, 'description')}
                  value={promotion?.text?.[language]?.description || ''}
                  placeholder="Type here"
                  showCharacterCount={false}
                  showToolbar={false}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
